import React from "react";

interface CustomPaginationProps {
  currentPage: number;
  totalPages: number;
  maxVisible?: number;             
  onPageChange: (page: number) => void;
}

export const CustomPagination: React.FC<CustomPaginationProps> = ({
  currentPage,
  totalPages,
  maxVisible = 5,
  onPageChange,
}) => {
  const getVisiblePages = (): number[] => {
    const pages: number[] = [];
    const half = Math.floor(maxVisible / 2);

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= half + 1) {
      for (let i = 1; i <= maxVisible; i++) pages.push(i);
      pages.push(totalPages);
    } else if (currentPage >= totalPages - half) {
      pages.push(1);
      for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      for (let i = currentPage - half; i <= currentPage + half; i++) {
        pages.push(i);
      }
      pages.push(totalPages);
    }
    return pages;
  };

  const visible = getVisiblePages();
  const btnBase: React.CSSProperties = {
    minWidth: 32,
    height: 32,
    lineHeight: "32px",
    textAlign: "center",
    margin: "0 4px",
    padding: 0,
    border: "1px solid #ccc",
        borderRadius: 16,
    backgroundColor: "transparent",
    color: "#444",
    cursor: "pointer",
    transition: "all .2s ease",
  };

  const btnHover: React.CSSProperties = {
    backgroundColor: "#f0f0f0",
  };

  const btnActive: React.CSSProperties = {
    ...btnBase,
    backgroundColor: "#78E01D",
    borderColor: "#78E01D",
    color: "#fff",
    cursor: "default",
    fontWeight: 500,
  };

  const btnDisabled: React.CSSProperties = {
    ...btnBase,
    color: "#bbb",
    borderColor: "#eee",
    cursor: "not-allowed",
    backgroundColor: "transparent",
  };

  const ellipsisStyle: React.CSSProperties = {
    margin: "0 8px",
    color: "#999",
    userSelect: "none",
  };

  return (
    <nav style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "12px 0" }}>
      <button
        style={currentPage === 1 ? btnDisabled : btnBase}
        onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Página anterior"
        onMouseEnter={(e) => Object.assign(e.currentTarget.style, btnHover)}
        onMouseLeave={(e) => Object.assign(e.currentTarget.style, currentPage === 1 ? btnDisabled : btnBase)}
      >
        ‹
      </button>

      {visible.map((p, i) => {
        const prev = visible[i - 1];
        const needsEllipsis = i > 0 && prev! + 1 < p;
        return (
          <React.Fragment key={p + "-" + i}>
            {needsEllipsis && <span style={ellipsisStyle}>…</span>}
            <button
              style={p === currentPage ? btnActive : btnBase}
              onClick={() => p !== currentPage && onPageChange(p)}
              disabled={p === currentPage}
              aria-label={`Página ${p}`}
              onMouseEnter={(e) =>
                p !== currentPage
                  ? Object.assign(e.currentTarget.style, btnHover)
                  : undefined
              }
              onMouseLeave={(e) =>
                p !== currentPage
                  ? Object.assign(e.currentTarget.style, btnBase)
                  : undefined
              }
            >
              {p}
            </button>
          </React.Fragment>
        );
      })}

      <button
        style={currentPage === totalPages ? btnDisabled : btnBase}
        onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Próxima página"
        onMouseEnter={(e) => Object.assign(e.currentTarget.style, btnHover)}
        onMouseLeave={(e) =>
          Object.assign(e.currentTarget.style, currentPage === totalPages ? btnDisabled : btnBase)
        }
      >
        ›
      </button>
    </nav>
  );
};
